import { useContext } from "react";
import { css } from "@emotion/css";
import APIContext from "../../contexts/APIContext";
import { Typography, images, Colors } from "@analyticsMD/quil";
import { styles as commonStyles } from "./common-styles";

import type { FC, ReactNode } from "react";
import RoundingButton from "../rounding/rounding-button";
import { alpha, Box } from "@mui/material";
import { common } from "@mui/material/colors";
import Configuration from "../../types/Configuration";

const styles = {
  ...commonStyles,
  patientInfoBody: css`
    font-size: 0.6875rem;
    margin-bottom: 1rem;
  `,
  patientInfoColumn: css`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    margin-top: 0.25rem;
  `,
};

const sxStyles = {
  patientInfoSection: {
    background: `url("${images.PatientBackground}") ${Colors.qlavender_80}`,
    backgroundSize: "cover",
    color: "white",
    padding: "1rem",
  },
  patientInfoSectionCompact: {
    boxSizing: "border-box",
    padding: "12px 16px",
    display: "flex",
    alignItems: "flex-start",
  },
  patientInfoHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  patientError: {
    alignItems: "start",
    marginTop: "0.5rem",
  },
  fullWidth: {
    width: "100%",
  },
  headerRow: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  },
  headerRowCompact: {
    alignItems: "flex-start",
  },
  patientNameHeader: {
    fontSize: "1.625rem",
    fontWeight: "bold",
    minHeight: "2rem",
    marginBottom: "1rem",
    lineHeight: "1.2",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  patientNameHeaderCompact: {
    marginBottom: 0,
    minHeight: "unset",
    flex: 1,
    minWidth: 0,
  },
  patientInfoContent: {
    fontSize: "0.75rem",
    lineHeight: "1.125",
  },
  errorCloseButton: {
    marginTop: "-1rem",
  },
  infoChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.75rem",
  },
  infoChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "0.1875rem 0.625rem",
    borderRadius: "999px",
    backgroundColor: alpha(common.white, 0.75),
  },
  infoChipLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.4,
    color: Colors.qlavender_80,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  infoChipSeparator: {
    fontSize: "0.75rem",
    lineHeight: 1.4,
    color: Colors.qlavender_80,
    opacity: 0.5,
    flexShrink: 0,
  },
  infoChipValue: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.4,
    color: Colors.qlavender_80,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
};

interface PatientInfoSectionProps {
  showRoundingMode?: boolean;
  showDemographics?: boolean;
  showError?: boolean;
  useSidebarView?: boolean;
  closeButtonCmpt?: ReactNode;
}
const PatientInfoSection: FC<PatientInfoSectionProps> = ({
  showError,
  showRoundingMode,
  showDemographics = false,
  useSidebarView = false,
  closeButtonCmpt = null,
}) => {
  const { configurationQuery, patientQuery } = useContext(APIContext);

  const configuration: Configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};
  let headerDisplay = "";
  if (patientQuery?.isSuccess && patientQuery.data) {
    headerDisplay = patientQuery.data.current_bed
      ? `${patientQuery.data.full_name} (${patientQuery.data.current_bed})`
      : patientQuery.data.full_name;
  }

  const demographics = () => {
    return (
      <div className={styles.patientInfoBody}>
        <div className={styles.patientInfoColumn}>
          <Typography sx={sxStyles.patientInfoContent}>
            DOB: {patientQuery.data.birth_date} ({patientQuery.data.age})
          </Typography>
          <Typography sx={sxStyles.patientInfoContent}>
            Admitting Dx: {patientQuery.data.admitting_diagnosis}
          </Typography>
          {configuration.payor_display_enabled && (
            <Typography sx={sxStyles.patientInfoContent}>
              Payor: {patientQuery.data.payor}
            </Typography>
          )}
          <Typography sx={sxStyles.patientInfoContent}>
            Att: {patientQuery.data.attending_provider}
          </Typography>
        </div>
      </div>
    );
  };

  // MVP (ENG-30652): a hardcoded pair of chips replacing the plain-text
  // demographics list, gated by config. Empty values collapse so a missing
  // LOS/Dx doesn't render a dangling chip.
  const infoChips = () => {
    if (!patientQuery?.data) return null;

    const chipData = [
      { label: "LOS", value: patientQuery.data.los },
      { label: "Admitting Dx", value: patientQuery.data.admitting_diagnosis },
    ].filter(({ value }) => value != null && `${value}`.trim() !== "");

    if (chipData.length === 0) return null;

    return (
      <Box sx={sxStyles.infoChipRow}>
        {chipData.map(({ label, value }) => (
          <Box key={label} sx={sxStyles.infoChip} title={`${label} · ${value}`}>
            <Box component="span" sx={sxStyles.infoChipLabel}>
              {label}
            </Box>
            <Box
              component="span"
              aria-hidden="true"
              sx={sxStyles.infoChipSeparator}
            >
              ·
            </Box>
            <Box component="span" sx={sxStyles.infoChipValue}>
              {value}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const chipsEnabled = !!configuration.qcard_patient_info_chips_enabled;

  const isCompactHeader = closeButtonCmpt != null;

  return (
    <Box
      sx={{
        ...sxStyles.patientInfoSection,
        ...(isCompactHeader ? sxStyles.patientInfoSectionCompact : {}),
      }}
    >
      <Box
        sx={{
          ...sxStyles.patientInfoHeader,
          ...(showError ? sxStyles.patientError : {}),
          ...(isCompactHeader ? sxStyles.fullWidth : {}),
        }}
      >
        <Box
          sx={{
            ...sxStyles.headerRow,
            ...(isCompactHeader ? sxStyles.headerRowCompact : {}),
          }}
        >
          <Typography
            sx={
              isCompactHeader
                ? {
                    ...sxStyles.patientNameHeader,
                    ...sxStyles.patientNameHeaderCompact,
                  }
                : sxStyles.patientNameHeader
            }
          >
            {headerDisplay}
          </Typography>
          {closeButtonCmpt && (
            <Box sx={showError ? sxStyles.errorCloseButton : undefined}>
              {closeButtonCmpt}
            </Box>
          )}
        </Box>
        {!showError && showDemographics && !chipsEnabled && demographics()}
        {!showError &&
          configuration &&
          configuration.rounding_mode_enabled &&
          showRoundingMode && (
            <Box>
              <RoundingButton hideRoundingButton={useSidebarView} />
            </Box>
          )}
        {/* Chips are the compact presentation, so unlike the verbose text list
            they also render in the sidebar/rounds view where showDemographics
            is false. */}
        {!showError && chipsEnabled && infoChips()}
      </Box>
    </Box>
  );
};

export default PatientInfoSection;
